import styles from "./CompaniesPage.module.scss";
import { CaretLeftIcon, EntryPointsIcon, FilterIcon, TagIcon } from "@/frontend/shared/assets/icons";
import { useEffect, useRef, useState } from "react";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { useQuery } from "@tanstack/react-query";
import { TableBody } from "./TableBody/TableBody";
import { Sidebar } from "@/frontend/components/Sidebar/Sidebar";
import { TCompany } from "../../DashboardPage/const/const";
import { TableLabels } from "./TableLabels";
import { Typography } from "@/frontend/shared/ui/Typography";
import { postSearchCompanies as postSearchCompaniesV2 } from "../model/services/postSearchCompaniesV2";
import { TActiveSort, TFormDataState, TTag } from "../model/types/types";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { useSearchParams } from "react-router-dom";
import { parseFilterParams, parseSortByParams, parseSortOrderParams } from "../lib/parseFilterParams";
import { initialFilterData } from "../const/const";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { EPostHogEventName } from "@/frontend/features/analytics/const/const";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import { getAllTags } from "../model/services/getAllTags";
import { fireAnalyticsEvent } from "@/frontend/features/analytics";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { getAllTagsForExpert } from "../model/services/getAllTagsForExpert";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import clsx from "clsx";
import { SquareNormalButton } from "@/frontend/shared/ui/Button/SquareNormalButton/SquareNormalButton";
import { Badge } from "@/frontend/shared/ui/Badge/Badge";
import { checkUrlForFilters } from "../lib/checkUrlForFilters";
import { countFilters } from "../lib/countFilters";
import { EContent } from "@/frontend/components/DashboardHeader/types/types";
import { extractOrganizationId } from "@/frontend/shared/lib/extractOrganizationId/extractOrganizationId";
import { useLocation } from "react-router";
import { AddCompaniesToPipelineModal } from "@/frontend/components/AddCompaniesToPipelineModal/AddCompaniesToPipelineModal";
import { Filters } from "@/frontend/components/FilterV2/ui/Filters";
import { ConditionRowFilterExtended } from "@/frontend/components/FilterV2/model/types";
import { AddTagsModalV2 } from "@/frontend/components/AddTagsModalV2/ui/AddTagsModalV2";
import { removeTagFromCompany } from "@/frontend/features/Company/model/removeTagFromCompany";
import { patchBulkAssignTags } from "@/frontend/features/Company/model/patchBulkAssignTags";
import { useDiscoverFilters } from "@/frontend/components/FilterV2/lib/useDiscoverFilters";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";

export function CompaniesPage() {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const filterMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { data: userData } = useMe();
  const userRole = userData?.entity;
  const isInvestor = userRole === ERoleAccess.INVESTOR;
  const isFounder = userRole === ERoleAccess.FOUNDER;
  const isExpert = userRole === ERoleAccess.EXPERT;
  const currentOrganizationId = isExpert ? extractOrganizationId(pathname) : userData?.organizationId;

  const { data: tagsDataResponse, refetch: refetchTags } = useQuery({
    queryKey: ["tags", searchParams, currentOrganizationId],
    queryFn: async () => {
      if (isInvestor || isFounder) {
        const res = await getAllTags();
        setFilterData((prevFilterData) => ({
          ...prevFilterData,
          formData: {
            ...prevFilterData.formData,
            tags: res.map((tag: TTag) => {
              return { label: tag.name, value: tag.name, isStatic: tag.isStatic };
            })
          }
        }));
        return res;
      } else if (isExpert) {
        if (!currentOrganizationId) return [];
        const res = await getAllTagsForExpert({
          id: currentOrganizationId || 0
        });
        setFilterData((prevFilterData) => ({
          ...prevFilterData,
          formData: {
            ...prevFilterData.formData,
            tags: res.map((tag: TTag) => {
              return { label: tag.name, value: tag.name };
            })
          }
        }));
        return res;
      }
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    enabled: isInvestor || (isExpert && currentOrganizationId !== undefined) || isFounder
  });

  const storedData = JSON.parse(localStorage.getItem(ELOCAL_STORAGE_KEY.COMPANIES_PAGE_PARAMS) || "{}");

  function findCombinedInitialData() {
    const noURLFilters = { ...initialFilterData, ...storedData.filterData };
    if (checkUrlForFilters(searchParams)) {
      return {
        ...noURLFilters,
        ...parseFilterParams(noURLFilters, searchParams)
      };
    }
    return { ...initialFilterData, ...noURLFilters };
  }

  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [shownModal, setShownModal] = useState("");
  const [page, setPage] = useState(searchParams.get("page") ? Number(searchParams.get("page")) : storedData.page || 1);
  const isMobile = useScreenWidth() <= 768;
  const [areFiltersOpen, setAreFiltersOpen] = useState(!isMobile);
  const [isCompaniesAddLoading, setIsCompaniesAddLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filterData, setFilterData] = useState<TFormDataState>(findCombinedInitialData() as TFormDataState);
  const { filters, setFilters } = useDiscoverFilters(ELOCAL_STORAGE_KEY.COMPANIES_PAGE_PARAMS + "V2");

  const [activeSort, setActiveSort] = useState<TActiveSort>({
    column: parseSortOrderParams(searchParams) ? parseSortByParams(searchParams) : null,
    direction: parseSortByParams(searchParams) ? parseSortOrderParams(searchParams) : null
  });

  const limit = 50;

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["searchedCompaniesV2", filters, activeSort, page, limit, tagsDataResponse],

    queryFn: async ({ signal }) => {
      const tagFilters: ConditionRowFilterExtended[] = [];
      const staticTagFilters: ConditionRowFilterExtended[] = [];
      const restFilters: ConditionRowFilterExtended[] = [];
      filters.forEach((f) => {
        if (f.type === "tag") {
          const correspondingTag = tagsDataResponse?.find((item: any) => item.name === f.value && !item.isStatic);
          if (correspondingTag) {
            tagFilters.push({ ...f, value: correspondingTag.id + "" });
          } else {
            staticTagFilters.push({ ...f, type: "staticTag" });
          }
        } else {
          restFilters.push(f);
        }
      });
      const res = await postSearchCompaniesV2(
        {
          page,
          limit: limit,
          filters: [...tagFilters, ...staticTagFilters, ...restFilters],
          sortBy: activeSort.column,
          sortOrder: activeSort.direction
        },
        { signal }
      );
      return res;
    },
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);
      snackbarConstructor("Error fetching companies", "error");
      reportErrorToSentry(error, "COMPANIES: /search/companiesFiltersV2");
      return false;
    }
  });

  function handleCardChange(id: number) {
    setSelectedRowIds((currentSelected) => {
      if (!currentSelected.includes(id)) {
        return [...currentSelected, id];
      }
      return currentSelected.filter((cardId) => cardId !== id);
    });
  }

  function triggerRefetch() {
    refetchTags();
    refetch();
  }

  const allCompanyIds = data?.companies?.map((item: TCompany) => item.id);

  function selectAllCompanies() {
    if (allCompanyIds.length === selectedRowIds.length) return setSelectedRowIds([]);
    setSelectedRowIds(allCompanyIds);
  }

  const addTagsFn = async (id: number, tags: string[]) => {
    await patchBulkAssignTags([id], tags);
  };

  const removeTagsFn = async (id: number, tagIds: number[]) => {
    await removeTagFromCompany({ tagIds, companyId: id });
  };

  useEffect(() => {
    if (data?.companies?.length === 0) {
      setPage(1);
    }
    fireAnalyticsEvent(EPostHogEventName.DISCOVER_SEARCH_COMPANIES);
  }, [data]);

  return (
    <div className={styles.wrapper}>
      <InnoveraCompanyHeader activeContent={EContent.DISCOVER} />
      <div className={styles.container}>
        <div className={clsx(styles.sidebarContainer, areFiltersOpen && !isMobile && styles.open)}>
          <Sidebar isOpen={areFiltersOpen} onClose={() => setAreFiltersOpen(false)} isMobile={isMobile}>
            <div className={styles.borderRight}>
              <Filters
                prefixId="discoverPage-"
                filters={filters}
                onCancel={() => setAreFiltersOpen(false)}
                onSave={(data) => {
                  setFilters(data);
                }}
                options={{
                  tag:
                    tagsDataResponse?.map((t: any) => ({
                      label: t.name,
                      value: t.name
                    })) || []
                }}
                wrap
                noControls
              />
            </div>
          </Sidebar>
        </div>
        <div className={styles.content}>
          <div className={styles.contentTitleBlock}>
            <div className={styles.contentTitle}>
              <NormalButton
                variant="secondary"
                size="large"
                onClick={() => setAreFiltersOpen((prev) => !prev)}
                className={styles.filtersButton}
                ref={filterMenuButtonRef}
                id="discoverPage-filterButton"
              >
                {areFiltersOpen ? <CaretLeftIcon /> : <FilterIcon />}
                {!!filters.length && (
                  <Badge className={styles.filtersBadge} bgColor="var(--innovera-orange500)">
                    {filters.length}
                  </Badge>
                )}
                <Typography variant="label-4" color="greyscale900">
                  Filters
                </Typography>
              </NormalButton>
              <Typography variant="heading-6" color="greyscale800">
                Discover
              </Typography>
            </div>
            {isInvestor && (
              <div className={styles.contentActionButtonsBlock}>
                {!!selectedRowIds.length && (
                  <div className={styles.checkboxContainer}>
                    <Typography color="greyscale900" variant="label-4">
                      {selectedRowIds.length} Selected
                    </Typography>
                  </div>
                )}
                <NormalButton
                  size="large"
                  variant="secondary"
                  className={styles.actionButton}
                  // onClick={addCompaniesToPipeline}
                  onClick={() => setIsAddModalOpen(true)}
                  isDisabled={selectedRowIds.length === 0 || isCompaniesAddLoading}
                  id="discoverPage-addToPipelineButton"
                >
                  <EntryPointsIcon />
                  Add {!isMobile && "to Pipeline"}
                </NormalButton>
                {!isMobile && (
                  <SquareNormalButton
                    size="large"
                    variant="secondary"
                    className={styles.actionButton}
                    isDisabled={selectedRowIds.length !== 1}
                    onClick={() => setShownModal("list")}
                    id="discoverPage-tagButton"
                  >
                    <TagIcon />
                    Tag
                  </SquareNormalButton>
                )}

                <AddTagsModalV2
                  triggerRefetch={triggerRefetch}
                  isOpen={shownModal === "list"}
                  onClose={() => setShownModal("")}
                  id={selectedRowIds[0]}
                  currentTags={
                    data?.companies
                      .filter((c: any) => selectedRowIds.includes(c.id))
                      .flatMap((c: any) => c.tags.map((tag: any) => tag.name)) || []
                  }
                  addTagsFn={addTagsFn}
                  removeTagsFn={removeTagsFn}
                  prefixId="discoverPage-"
                />

                <AddCompaniesToPipelineModal
                  prefixId="discoverPage-"
                  triggerRefetch={triggerRefetch}
                  isOpen={isAddModalOpen}
                  onClose={() => {
                    setIsAddModalOpen(false);
                    setSelectedRowIds([]);
                  }}
                  ids={selectedRowIds}
                />
              </div>
            )}
          </div>
          <div className={styles.contentTableBlockInvestor}>
            <TableLabels
              onChange={selectAllCompanies}
              value={selectedRowIds.length === allCompanyIds?.length && selectedRowIds.length > 0}
              activeSort={activeSort}
              setActiveSort={setActiveSort}
              isFounder={isFounder || isExpert}
            />
            <TableBody
              selected={selectedRowIds}
              onChange={handleCardChange}
              data={data || []}
              triggerRefetch={triggerRefetch}
              setPage={setPage}
              limit={limit}
              currentPage={page}
              isLoading={isFetching}
              isFounder={isFounder || isExpert}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
